import { useState, useEffect } from 'react';

// Define user interface
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

// Define auth context type
export interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

// Mock authentication hook for development
export const useAuth = (): AuthContextType => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    // In a real app, this would be an API call
    // For now, we'll simulate a successful login with a mock user
    const mockUser: User = {
      id: '123456',
      email,
      firstName: 'John',
      lastName: 'Doe',
      role: 'firm',
    };

    setUser(mockUser);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(mockUser));
  };

  // Logout function
  const logout = (): void => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('currentTenant');
  };

  return {
    isAuthenticated,
    user,
    login,
    logout,
  };
};

export default useAuth;
